<template>
  <div class="page-reset_password com_bg1">
    <div id="wrapper1">
      <img
        src="/WITH_AG_PORTAL/images/with-kaunet_logo2.png"
        alt="ウィズカウネット エージェントポータル"
        class="with-kaunet_logo2"
        style="margin-top: 0"
      />
      <div v-if="isSendMail" class="com_form_contet1">
        <div class="inner1">
          <h2 class="title1 com_mgb30">
            パスワード再設定のメールを送信しました
          </h2>

          <p class="com_center1 com_mgb30">
            パスワード再設定はまだ完了しておりません。<br />メール本文に記載のURLからお手続きを完了させてください。<br />メールが届かない場合はエージェントコールまでお問い合わせください。
          </p>

          <router-link to="/login" class="com_button1"
            >ログインページ</router-link
          >
        </div>
      </div>
      <div v-else class="com_form_contet1">
        <div class="inner1">
          <h2 class="title1">パスワード再設定</h2>

          <p class="com_left1 com_mgb30">
            パスワードを再設定するために、エージェントコードとログインIDで使用されているメールアドレスを入力してください。
            メールアドレスに仮登録URLをお送りします。
          </p>

          <div class="form1">
            <div class="group1">
              <label for="agentCode">エージェントコード</label>
              <AlphanumericInput
                id="agentCode"
                v-model="agCode"
                type="text"
                laceholder=""
                maxlength="20"
              />
            </div>
            <div class="group1">
              <label for="email">メールアドレス</label>
              <input
                id="mailAddress"
                placeholder="sample@agweb.kaunet.com"
                v-model="email"
                maxlength="255"
              />
            </div>
            <!-- エラーメッセージの表示エリア -->
            <div v-if="errorMessage" class="group1">
              <label></label>
              <div class="error-message">
                {{ errorMessage }}
              </div>
            </div>

            <p class="com_left1 com_mgt20">
              ※仮登録URLが届かない場合はエージェントコード、もしくはメールアドレスが異なっている可能性があります。カウネットからのご案内、もしくは管理者からのご案内をご確認ください。<br />
            </p>
            <button
              type="submit"
              @click="sendResetMail"
              :disabled="isProcessing"
            >
              送信
            </button>
            <div class="com_center1">
              <router-link to="/login" class="com_link1 com_fs15 arr1"
                >ログインページへ戻る</router-link
              >
            </div>
          </div>
        </div>
      </div>

      <p class="com_copyright1">© 2025 Kaunet</p>
    </div>
  </div>
</template>

<script lang="ts">
import { AxiosError } from "axios";
import { defineComponent, ref } from "vue";
import apiClient from "@/util/apiClient";
import { formatMessage, sanitizeInput } from "@/Common";
import AlphanumericInput from "../controls/AlphanumericInput.vue";
import messages from "@/assets/messages.json";
import { AppConstants } from "@/constants/AppConstants";

export default defineComponent({
  name: "SendPwdResetMailComponent",
  components: {
    AlphanumericInput,
  },
  setup() {
    const agCode = ref("");
    const email = ref("");
    const errorMessage = ref(""); // errorMessage をリアクティブなデータとして定義
    const isSendMail = ref(false);
    const isProcessing = ref(false);

    // 必須入力チェック
    const validateInput = (): boolean => {
      const sanitizedAgCode = sanitizeInput(agCode.value);
      const sanitizedEmail = sanitizeInput(email.value);
      if (!sanitizedAgCode) {
        errorMessage.value = formatMessage(
          messages.ERR_0001,
          "エージェントコード"
        );
        return false;
      } else if (!sanitizedEmail) {
        errorMessage.value = formatMessage(messages.ERR_0001, "メールアドレス");
        return false;
      } else {
        errorMessage.value = ""; // エラーがない場合はクリア
        return true;
      }
    };

    // メールアドレスチェック
    const validateEmail = (): boolean => {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isHalfWidth = /^[\x20-\x7E]+$/;
      const sanitizedEmail = sanitizeInput(email.value);

      if (!isHalfWidth.test(sanitizedEmail)) {
        errorMessage.value = formatMessage(messages.ERR_0018, "メールアドレス");
        return false;
      } else if (!emailPattern.test(sanitizedEmail)) {
        errorMessage.value = formatMessage(messages.ERR_0020, "メールアドレス");
        return false;
      } else {
        errorMessage.value = "";
        return true;
      }
    };

    // パスワードリセットメール送信
    const sendResetMail = async () => {
      isProcessing.value = true;
      try {
        // 入力チェック
        if (!validateInput()) {
          return;
        }
        // Emailチェック
        if (!validateEmail()) {
          return;
        }

        // パスワードリセットメール送信
        await apiClient.post("/api/pwdReset/sendResetMail", {
          systemKbn: AppConstants.SYSTEM_KBN_WITH,
          agCode: sanitizeInput(agCode.value),
          emailAddress: sanitizeInput(email.value),
        });

        isSendMail.value = true;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          switch (axiosError.response.status) {
            case 401:
              errorMessage.value = messages.ERR_USER004;
              break;
            case 500:
              errorMessage.value = messages.ERR_SYS999;
              break;
            default:
              errorMessage.value = messages.ERR_SYS001;
          }
        } else if (axiosError.request) {
          errorMessage.value = messages.ERR_SYS001;
        } else {
          errorMessage.value = messages.ERR_SYS002;
          if (process.env.VUE_APP_DEBUG === "true") {
            console.log("Error: ", axiosError.message);
          }
        }
      } finally {
        isProcessing.value = false;
      }
    };
    return {
      agCode,
      email,
      errorMessage,
      validateInput,
      sendResetMail,
      isSendMail,
      isProcessing,
    };
  },
});
</script>

<style scoped>
.page-reset_password #wrapper1 {
  /* グレー背景のまま、全体を少し下げる */
  padding-top: 30px; /* ←好きな高さに調整 */
}
</style>
